import { useState, useRef, useEffect, Children } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Send, Square } from 'lucide-react'
import { api } from '../../api/client'
import { useConversations } from '../../contexts/ConversationContext'
import ReactMarkdown from 'react-markdown'

// Custom ReactMarkdown components. Tags a `<p>` as a "label paragraph"
// (styled as a small block in CSS) when its meaningful content is a
// single `<strong>` element — e.g. "**🔥 Prioridades**" on its own line
// in a briefing. Mid-sentence bold ("texto **bold** texto") must NOT
// trigger this, otherwise the strong gets shrunk + display block and
// breaks the paragraph layout. Text-node filtering is the key — CSS's
// `:only-child` can't express this; React introspection can.
const chatMarkdownComponents = {
  p({ children, ...props }: { children?: React.ReactNode }) {
    const arr = Children.toArray(children).filter(c => {
      if (typeof c === 'string') return c.trim().length > 0
      return true
    })
    const isLabelOnly =
      arr.length === 1 &&
      typeof arr[0] === 'object' &&
      arr[0] !== null &&
      (arr[0] as { type?: unknown }).type === 'strong'
    return isLabelOnly ? (
      <p className="chat-prose-label" {...props}>{children}</p>
    ) : (
      <p {...props}>{children}</p>
    )
  },
}
import { CalendarProposalCard, type CalendarProposal } from './CalendarProposalCard'
import { EmailLabelProposalCard, type EmailLabelProposal } from './EmailLabelProposalCard'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  created_at?: string
  calendar_proposals?: CalendarProposal[]
  email_label_proposals?: EmailLabelProposal[]
}

// WhatsApp-style time helpers. Kept local — used only here.
function formatTime(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
}
function dayKey(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}
function dayLabel(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) return 'Hoje'
  const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  if (d.toDateString() === y.toDateString()) return 'Ontem'
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  if (d.getFullYear() !== now.getFullYear()) {
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
  }
  return `${d.getDate()} ${months[d.getMonth()]}`
}

// ── Atomic note card parsing ──────────────────────────────────────────────

interface AtomicCard {
  label: string
  headline: string
  body: string
  isStreaming: boolean
}
interface ParsedCards {
  prelude: string
  sections: AtomicCard[]
}

// Extracts the first **bold line** as a headline, returns remaining body.
function extractHeadlineAndBody(bodyText: string): { headline: string; body: string } {
  const trimmed = bodyText.trimStart()
  const m = trimmed.match(/^\*\*([^*\n]+)\*\*\s*\n?/)
  if (m) {
    return { headline: m[1].trim(), body: trimmed.slice(m[0].length).trim() }
  }
  return { headline: '', body: trimmed }
}

// Parses markdown into atomic note cards (one per ## / ### section).
// Returns null when there are no ## headers (render as plain prose instead).
// When streaming=true, the last card is marked isStreaming for skeleton display.
function parseIntoCards(content: string, streaming = false): ParsedCards | null {
  const lines = content.split('\n')
  const raw: { header: string; bodyLines: string[] }[] = []
  const preludeLines: string[] = []
  let curHeader: string | null = null
  let curLines: string[] = []

  for (const line of lines) {
    const m = line.match(/^#{2,3}\s+(.+)$/)
    if (m) {
      if (curHeader !== null) raw.push({ header: curHeader, bodyLines: curLines })
      curHeader = m[1].trim()
      curLines = []
    } else if (curHeader !== null) {
      curLines.push(line)
    } else {
      preludeLines.push(line)
    }
  }
  if (curHeader !== null) raw.push({ header: curHeader, bodyLines: curLines })
  if (raw.length < 1) return null

  return {
    prelude: preludeLines.join('\n').trim(),
    sections: raw.map((s, i) => {
      const { headline, body } = extractHeadlineAndBody(s.bodyLines.join('\n'))
      return { label: s.header, headline, body, isStreaming: streaming && i === raw.length - 1 }
    }),
  }
}

// ── Card renderer ─────────────────────────────────────────────────────────

function AtomicCard({ label, headline, body, isStreaming }: AtomicCard) {
  return (
    <div className="atomic-note-card">
      <div className="atomic-note-head">
        <span className="atomic-note-lbl">{label}</span>
        {headline && <span className="atomic-note-lead">{headline}</span>}
      </div>
      {body ? (
        <div className="chat-prose atomic-note-body">
          <ReactMarkdown components={chatMarkdownComponents}>{body}</ReactMarkdown>
        </div>
      ) : isStreaming ? (
        <div className="atomic-note-skeleton">
          <div className="sk-line" style={{ width: '85%' }} />
          <div className="sk-line" style={{ width: '65%' }} />
          <div className="sk-line" style={{ width: '75%' }} />
        </div>
      ) : null}
    </div>
  )
}

function renderCards(parsed: ParsedCards) {
  return (
    <>
      {parsed.prelude && (
        <div className="chat-prose" style={{ marginBottom: '12px' }}>
          <ReactMarkdown components={chatMarkdownComponents}>{parsed.prelude}</ReactMarkdown>
        </div>
      )}
      <div className="atomic-cards-feed">
        {parsed.sections.map((card, i) => (
          <AtomicCard key={i} {...card} />
        ))}
      </div>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────

export function ChatPage() {
  const { conversationId } = useParams()
  const navigate = useNavigate()
  const {
    activeConvId,
    setActiveConvId,
    addConversation,
    pendingAgentConversations,
    normalConversations,
    archivedConversations,
    streamStates,
    sendMessage,
    abortStream,
    clearStreamState,
  } = useConversations()

  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [agentWaiting, setAgentWaiting] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const agentPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const skipNextLoadRef = useRef(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const stream = activeConvId ? streamStates.get(activeConvId) : undefined
  const isStreaming = !!stream?.isStreaming
  const prevIsStreamingRef = useRef(isStreaming)

  useEffect(() => {
    if (conversationId) {
      setActiveConvId(conversationId)
    } else {
      setActiveConvId(null)
    }
  }, [conversationId])

  useEffect(() => {
    if (!activeConvId) {
      setMessages([])
      setAgentWaiting(false)
      stopAgentPoll()
      return
    }
    stopAgentPoll()
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false
      return
    }
    loadMessages(activeConvId).then(msgs => {
      if (msgs.length === 0 && conversationId === activeConvId && !streamStates.get(activeConvId)) {
        startAgentPoll(activeConvId)
      }
    })
  }, [activeConvId])

  useEffect(() => {
    const wasStreaming = prevIsStreamingRef.current
    prevIsStreamingRef.current = isStreaming
    if (!activeConvId) return
    if (wasStreaming && !isStreaming && stream) {
      if (stream.error || !stream.assistantContent) {
        loadMessages(activeConvId)
        return
      }
      loadMessages(activeConvId).finally(() => clearStreamState(activeConvId))
    }
  }, [isStreaming, activeConvId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, stream?.assistantContent])

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [input])

  function stopAgentPoll() {
    if (agentPollRef.current) { clearInterval(agentPollRef.current); agentPollRef.current = null }
    setAgentWaiting(false)
  }

  function startAgentPoll(convId: string) {
    setAgentWaiting(true)
    agentPollRef.current = setInterval(async () => {
      if (streamStates.get(convId)?.isStreaming) return
      try {
        const msgs = await api.get<Message[]>(`/chat/conversations/${convId}/messages`)
        if (msgs.length > 0) { setMessages(msgs); stopAgentPoll() }
      } catch { /* keep polling */ }
    }, 2000)
  }

  async function loadMessages(convId: string): Promise<Message[]> {
    try {
      const msgs = await api.get<Message[]>(`/chat/conversations/${convId}/messages`)
      setMessages(msgs)
      return msgs
    } catch { setMessages([]); return [] }
  }

  async function handleSend() {
    if (!input.trim() || isStreaming) return
    const userMessage = input.trim()
    setInput('')
    stopAgentPoll()

    let convId = activeConvId
    if (!convId) {
      try {
        const conv = await api.post<{
          id: string
          title: string | null
          agent_id: string | null
          is_archived: boolean
          trigger: string | null
          seen_at: string | null
          last_message_snippet: string | null
          last_message_at: string | null
          created_at: string
        }>('/chat/conversations', {})
        convId = conv.id
        addConversation(conv)
        skipNextLoadRef.current = true
        setActiveConvId(convId)
        if (!window.location.pathname.startsWith(`/chat/${convId}`)) {
          navigate(`/chat/${convId}`, { replace: true })
        }
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'Erro desconhecido'
        setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: `Erro: ${errMsg}` }])
        return
      }
    }

    sendMessage(convId, userMessage, userMessage)
  }

  const mobileTitle = (() => {
    if (!activeConvId) return 'Nova conversa'
    const hit =
      normalConversations.find((c) => c.id === activeConvId) ||
      pendingAgentConversations.find((c) => c.id === activeConvId) ||
      archivedConversations.find((c) => c.id === activeConvId)
    return hit?.title || 'Nova conversa'
  })()

  return (
    <div className="flex flex-col flex-1 overflow-hidden" style={{ background: 'var(--cream)' }}>
      <header
        className="md:hidden flex items-center gap-3 px-3 py-3"
        style={{
          background: 'var(--cream)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <button
          onClick={() => navigate('/chat')}
          className="p-1.5 -ml-1.5 rounded transition-colors"
          style={{ color: 'var(--ink)' }}
          aria-label="Voltar às conversas"
        >
          <ArrowLeft size={20} strokeWidth={1.75} />
        </button>
        <span
          className="truncate text-sm font-medium"
          style={{ color: 'var(--ink)' }}
        >
          {mobileTitle}
        </span>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6 md:px-8">
        {messages.length === 0 && !stream && (
          <div className="flex items-center justify-center h-full text-center">
            {agentWaiting ? (
              <div>
                <div className="flex gap-1.5 justify-center mb-3">
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--ink-muted)', animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--ink-muted)', animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--ink-muted)', animationDelay: '300ms' }} />
                </div>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>A processar...</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium mb-1" style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: 'var(--ink)' }}>
                  se<span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Guru</span>
                </p>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Em que posso ajudar?</p>
              </div>
            )}
          </div>
        )}

        {(() => {
          const renderAssistantBody = (msg: Message) => {
            const parsed = parseIntoCards(msg.content || '')
            const proposals = msg.calendar_proposals || []
            const labelProposals = msg.email_label_proposals || []

            const handleProposalUpdate = (updated: CalendarProposal) => {
              setMessages(prev =>
                prev.map(m =>
                  m.id === msg.id
                    ? { ...m, calendar_proposals: (m.calendar_proposals || []).map(p => p.id === updated.id ? updated : p) }
                    : m,
                ),
              )
            }
            const handleLabelProposalUpdate = (updated: EmailLabelProposal) => {
              setMessages(prev =>
                prev.map(m =>
                  m.id === msg.id
                    ? { ...m, email_label_proposals: (m.email_label_proposals || []).map(p => p.id === updated.id ? updated : p) }
                    : m,
                ),
              )
            }
            const proposalCards = (proposals.length > 0 || labelProposals.length > 0) && (
              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {proposals.map(p => (
                  <CalendarProposalCard key={p.id} proposal={p} onChange={handleProposalUpdate} />
                ))}
                {labelProposals.map(p => (
                  <EmailLabelProposalCard key={p.id} proposal={p} onChange={handleLabelProposalUpdate} />
                ))}
              </div>
            )

            if (parsed) {
              return (
                <>
                  {renderCards(parsed)}
                  {proposalCards}
                </>
              )
            }

            return (
              <>
                <div className="chat-prose">
                  <ReactMarkdown components={chatMarkdownComponents}>{msg.content || '...'}</ReactMarkdown>
                </div>
                {proposalCards}
              </>
            )
          }

          const items: React.ReactNode[] = []
          let prevDay: string | null = null

          for (const msg of messages) {
            const thisDay = dayKey(msg.created_at)
            if (thisDay && thisDay !== prevDay) {
              items.push(
                <div key={`div-${thisDay}-${msg.id}`} className="flex justify-center my-2">
                  <span
                    className="text-[10px] px-3 py-1 rounded-full uppercase tracking-wider"
                    style={{
                      background: 'rgba(0,0,0,0.04)',
                      color: 'var(--ink-muted)',
                      fontWeight: 500,
                    }}
                  >
                    {dayLabel(msg.created_at)}
                  </span>
                </div>,
              )
              prevDay = thisDay
            }

            if (msg.role === 'user') {
              items.push(
                <div key={msg.id} className="flex flex-col items-end">
                  <div
                    className="max-w-[75%] rounded-2xl px-4 py-2.5 text-sm"
                    style={{ background: 'var(--accent-light)', color: 'white' }}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {msg.created_at && (
                    <span
                      className="text-[10px] mt-1 mr-1 tabular-nums"
                      style={{ color: 'var(--ink-muted)' }}
                    >
                      {formatTime(msg.created_at)}
                    </span>
                  )}
                </div>,
              )
            } else {
              items.push(
                <div key={msg.id} className="max-w-3xl">
                  {renderAssistantBody(msg)}
                  {msg.created_at && (
                    <span
                      className="block text-[10px] mt-1.5 tabular-nums"
                      style={{ color: 'var(--ink-muted)' }}
                    >
                      {formatTime(msg.created_at)}
                    </span>
                  )}
                </div>,
              )
            }
          }

          return items
        })()}

        {/* Live stream — user bubble + accumulating assistant reply */}
        {stream && stream.isStreaming && (
          <>
            <div className="flex justify-end">
              <div
                className="max-w-[75%] rounded-2xl px-4 py-2.5 text-sm"
                style={{ background: 'var(--accent-light)', color: 'white' }}
              >
                <p className="text-sm whitespace-pre-wrap">{stream.userDisplay}</p>
              </div>
            </div>
            <div className="max-w-3xl">
              {!stream.assistantContent ? (
                <div className="flex gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--ink-muted)', animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--ink-muted)', animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--ink-muted)', animationDelay: '300ms' }} />
                </div>
              ) : (() => {
                const streamParsed = parseIntoCards(stream.assistantContent, true)
                if (streamParsed) return renderCards(streamParsed)
                return (
                  <div className="chat-prose">
                    <ReactMarkdown components={chatMarkdownComponents}>{stream.assistantContent}</ReactMarkdown>
                  </div>
                )
              })()}
            </div>
          </>
        )}
        {stream && !stream.isStreaming && (stream.error || !stream.assistantContent) && (
          <div
            className="max-w-3xl rounded-lg p-3 flex items-start gap-3 text-sm"
            style={{
              background: 'rgba(139,105,20,0.06)',
              border: '1px solid rgba(139,105,20,0.2)',
              color: 'var(--accent-warm)',
            }}
          >
            <span className="shrink-0" style={{ fontWeight: 500 }}>⚠</span>
            <div className="flex-1 min-w-0">
              <div style={{ color: 'var(--ink)' }}>
                {stream.error
                  ? `Não consegui responder: ${stream.error}`
                  : 'Sem resposta. Tenta de novo.'}
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => {
                    if (!activeConvId) return
                    const retry = stream.userDisplay
                    clearStreamState(activeConvId)
                    sendMessage(activeConvId, retry, retry)
                  }}
                  className="px-3 py-1 rounded text-xs transition-colors"
                  style={{ background: 'var(--accent)', color: 'white' }}
                >
                  Tentar de novo
                </button>
                <button
                  onClick={() => activeConvId && clearStreamState(activeConvId)}
                  className="px-3 py-1 rounded text-xs transition-colors"
                  style={{ background: 'transparent', color: 'var(--ink-muted)', border: '1px solid var(--border)' }}
                >
                  Dispensar
                </button>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="max-w-3xl mx-auto space-y-2">
          <div className="flex gap-2 items-end">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSend()
                }
              }}
              rows={1}
              placeholder={isStreaming ? 'A gerar resposta...' : 'Escreve a tua mensagem...'}
              className="flex-1 border rounded-lg px-4 py-2.5 text-sm focus:outline-none transition-colors resize-none overflow-y-auto"
              style={{
                borderColor: 'var(--border)',
                background: 'white',
                color: 'var(--ink)',
                fontFamily: 'var(--sans)',
                lineHeight: '1.5',
                maxHeight: '140px',
                minHeight: '42px',
              }}
            />
            {isStreaming ? (
              <button
                onClick={() => activeConvId && abortStream(activeConvId)}
                className="rounded-lg px-4 py-2.5 transition-colors shrink-0"
                style={{ background: 'var(--ink)', color: 'var(--cream)' }}
                title="Parar resposta"
              >
                <Square size={16} strokeWidth={1.5} />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="rounded-lg px-4 py-2.5 transition-colors disabled:opacity-30 shrink-0"
                style={{ background: 'var(--ink)', color: 'var(--cream)' }}
              >
                <Send size={16} strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
